<template>
  <div class="do-templates-menu" :class="{ in: show }">
    <div class="border-b border-gray-100 p-4">
      <search-input
        v-model="search"
        no-margin
        no-padding
        name="search-do-templates"
      />
    </div>
    <ul ref="listRef" class="max-h-60 overflow-auto p-2" @scroll="handleScroll">
      <loader v-if="!templates.length" :is-loading="loading" />
      <li
        v-for="template in templates"
        :key="template.id"
        v-tooltip="{
          content:
            template.name.length > MAX_TEMLATE_NAME_LENGTH && showTooltip
              ? template.name
              : '',
          placement: 'bottom',
          followCursor: 'vertical',
        }"
        class="cursor-pointer truncate p-2 whitespace-nowrap text-gray-700 hover:bg-gray-100"
        @mouseenter="checkContentWidth"
        @click="emit('template:select', template)"
      >
        <div class="flex justify-between space-x-2">
          <p
            class="overflow-hidden text-ellipsis whitespace-nowrap"
            data-cy="do-templates-dropdown-option-text"
          >
            {{ template.name }}
          </p>
          <span class="self-center text-xxs text-gray-300">
            {{ formatDateCustom(template.updated_at) }}
          </span>
        </div>
      </li>
      <scroll-sentinel
        :can-load-more="canLoadMore"
        :is-loading="loading"
        :root="listRef"
        @load-more="getAllTemplates(nextPage)"
      />
      <li v-if="!templates.length" class="px-3 py-2 text-gray-700">
        {{ $t("ORCHESTRATION.NO_TEMPLATES") }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";

import { storeToRefs } from "pinia";

import { formatDateCustom } from "@/helpers/formatting";
import { getParamsWithPagination } from "@/helpers/pagination";
import { usePromiseWrapper } from "@/hooks/common";
import { useDealsControlPanelStore } from "@/stores/dealsControlPanel";

import ScrollSentinel from "@/components/ui/inputs/ScrollSentinel.vue";

import type { IOrchestrationTemplate } from "@/models/orchestration";

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const dealsControlPanelStore = useDealsControlPanelStore();
const { templates, templatesMeta } = storeToRefs(dealsControlPanelStore);

const listRef = useTemplateRef<HTMLUListElement>("listRef");
const isTextTruncated = ref(false);

const search = ref("");
const showTooltip = ref(true);
const ITEMS_PER_PAGE = 25;
const MAX_TEMLATE_NAME_LENGTH = 43;

const emit = defineEmits<{
  "template:select": [template: IOrchestrationTemplate];
}>();

const nextPage = computed(() =>
  templatesMeta.value?.current_page ? templatesMeta.value?.current_page + 1 : 1
);

const canLoadMore = computed(
  () =>
    !!templates.value.length &&
    templatesMeta.value?.last_page !== templatesMeta.value?.current_page
);

const { fetchWrapper: getAllTemplates, loading } = usePromiseWrapper(
  async (page = 1) => {
    const payload = {
      params: getParamsWithPagination(
        { search: search.value || null },
        page,
        ITEMS_PER_PAGE
      ),
    };
    await dealsControlPanelStore.getDoTemplates(payload);
  }
);

const handleScroll = () => {
  // Hide tooltip when scrolling
  showTooltip.value = false;
  showTooltip.value = true;
};

const checkContentWidth = (event: MouseEvent) => {
  const { offsetWidth, scrollWidth } = event.target as HTMLElement;
  isTextTruncated.value = offsetWidth < scrollWidth;
};

watch(search, () => getAllTemplates());

onMounted(getAllTemplates);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.do-templates-menu {
  @apply absolute right-0 w-100 rounded bg-white shadow-lg transition-all;
}

.do-templates-menu:not(.in) {
  top: calc(100% - 0.25rem);
  @apply -z-1 opacity-0;
}

.do-templates-menu.in {
  top: calc(100% + 0.25rem);
  @apply z-40;
}
</style>
